<template>
	<ui-modal @toggle="$emit('toggle')">
		<div class="flex flex-col gap-5" v-if="!loading">
			<Head :title="user.first_name + ' ' + user.last_name" />
			<ui-title textSize="big" textPosition="left" class="lg:col-span-2 text-slate-700">
				<template v-slot:text>
					<p contenteditable="true" @input="form.first_name = $event.target.textContent">
						{{ form.first_name }}
					</p>
					<p contenteditable="true" @input="form.last_name = $event.target.textContent">
						{{ form.last_name }}
					</p>
					<div class="flex justify-center text-xl lg:text-lg gap-3">
						<a v-if="user.phone" :href="'tel:' + user.phone" class="p-2 cursor-pointer text-slate-500 hover:text-slate-700">
							<i class="fas fa-phone"></i>
						</a>
						<a v-if="user.email" :href="'mailto:' + user.email" class="p-2 cursor-pointer text-slate-500 hover:text-slate-700">
							<i class="fas fa-envelope"></i>
						</a>
					</div>
				</template>
				<template v-slot:actions>
					<ui-button @click="generateReport()" :loading="reportLoading">
						<template v-slot:icon><i class="fas fa-file-download"></i></template>
						<template v-slot:text>Rapport</template>
					</ui-button>
				</template>
			</ui-title>
			<form-validation-errors class="lg:col-span-2"></form-validation-errors>
			<form @submit.prevent="doUpdate()" @keyup.enter="doUpdate()" class="flex flex-col lg:grid lg:grid-cols-2 lg:col-span-2 gap-3 w-full">
				<!-- PHONE + EMAIL -->
				<form-input v-model="form.phone" placeholder="Numéro de téléphone"> </form-input>
				<form-input v-model="form.email" placeholder="E-Mail"> </form-input>
				<form-input v-model="form.password" placeholder="Nouveau mot de passe" type="password" class="lg:col-span-2"> </form-input>
				<form-input v-model="form.confirm_password" placeholder="Confirmer le mot de passe" type="password" class="lg:col-span-2">
				</form-input>
				<form-select placeholder="Role" @valueChange="form.role = $event" class="col-span-2" :items="roles" :value="form.role">
					<template v-if="form.role === 'user'" v-slot:icon><i class="far fa-user"></i></template>
					<template v-else-if="form.role === 'admin'" v-slot:icon><i class="fas fa-user-shield"></i></template>
					<template v-else v-slot:icon><i class="fa-solid fa-user"></i></template>
				</form-select>
			</form>
			<ui-button @click="doUpdate" :loading="formLoading" class="border-2 border-blue-400">
				<template v-slot:icon><i class="fas fa-edit"></i></template>
				<template v-slot:text>Modifier</template>
			</ui-button>
		</div>
		<div v-else class="h-full w-full flex items-center justify-center text-2xl">
			<i class="fas fa-circle-notch fa-spin"></i>
		</div>
	</ui-modal>
</template>

<script setup>
	import { ref, onMounted } from 'vue';
	import { Head, useForm } from '@inertiajs/vue3';
	import { toast } from 'vue3-toastify';
	import axios from 'axios';

	const emit = defineEmits(['toggle', 'updateClicked']);

	const props = defineProps({
		uid: {
			type: [String, Number],
			required: true,
		},
	});

	const loading = ref(true);
	const formLoading = ref(false);
	const user = ref({});
	const reportLoading = ref(false);

	const roles = ref([
		{ value: 'admin', name: 'Administrateur' },
		{ value: 'user', name: 'Utilisateur' },
		{ value: 'headExec', name: 'Chef de service' },
		{ value: 'doctor', name: 'Médecin' },
		{ value: 'careExec', name: 'Cadre de soin' },
	]);

	const form = useForm({
		first_name: '',
		last_name: '',
		email: '',
		phone: '',
		password: null,
		confirm_password: null,
		role: null,
	});

	const getUserInfo = () => {
		loading.value = true;
		axios
			.get(route('admin.users.get', props.uid))
			.then((response) => {
				user.value = response.data;
				form.first_name = user.value.first_name;
				form.last_name = user.value.last_name;
				form.role = user.value.role;
				form.email = user.value.email;
				form.phone = user.value.phone;
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				loading.value = false;
			});
	};

	const doUpdate = () => {
		toast.info('Utilisateur en cours de modification');
		formLoading.value = true;
		form.post(route('admin.users.update', user.value.id), {
			preserveScroll: true,
			onSuccess: () => {
				toast.success('Utilisateur modifié avec succès !');
				emit('toggle');
			},
			onFinish: () => {
				formLoading.value = false;
			},
		});
	};

	const generateReport = async () => {
		reportLoading.value = true;
		try {
			const response = await axios.get(route('log.report', user.value.id), { responseType: 'blob' });
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute(
				'download',
				user.value.first_name + ' ' + user.value.last_name + ' - Rapport ' + new Date().toLocaleDateString('fr-FR') + '.xlsx',
			);
			document.body.appendChild(link);
			link.click();
		} catch (error) {
			toast.error('Erreur lors de la génération du rapport');
		}
		reportLoading.value = false;
	};

	onMounted(() => {
		getUserInfo();
	});
</script>
